import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { addDays, endOfDay, format, formatISO } from 'date-fns';
import "react-datepicker/dist/react-datepicker.css";

import UsersAPI from '../../../../api/Users';

const TITLES = {
    grant: "Grant tokens",
    convert: "Convert tokens to another type",
    change_expiry: "Change expiration",
    void: "Void tokens",
};

const DEFAULT_EXPIRY = {
    grant: "type_rule",
    convert: "keep",
    change_expiry: "custom",
};

/** What the type's own expiration rule would give, counted from today. */
const ruleText = (type) => {
    if (!type) return "the token type's rule";
    if (type.expires_in_days > 0) {
        return `${type.expires_in_days} days from today (${format(addDays(new Date(), type.expires_in_days), "MM/dd/yyyy")})`;
    }
    return "no expiration (this type never expires)";
};

const typeLabel = (type) => {
    let label = `${type.product_name} - $${Number(type.price).toFixed(2)}`;
    if (type.services?.length) label += ` (books: ${type.services.join(", ")})`;
    return label;
};

/**
 * One modal for the four staff token actions.
 *
 * @param {string} props.action - grant | convert | change_expiry | void
 * @param {array} props.selectedTokens - token rows from /user/tokens (not used for grant)
 * @param {array} props.types - rows from /user/tokens/types
 */
export const TokenActionModal = ({ show, action, userId, selectedTokens = [], types = [], onClose, onDone }) => {
    const [variantId, setVariantId] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [expiryMode, setExpiryMode] = useState(DEFAULT_EXPIRY[action]);
    const [customDate, setCustomDate] = useState(null);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) {
            setVariantId("");
            setQuantity(1);
            setExpiryMode(DEFAULT_EXPIRY[action]);
            setCustomDate(null);
            setReason("");
            setError(null);
            setSubmitting(false);
        }
    }, [show, action]);

    const selectedType = useMemo(
        () => types.find(type => String(type.variant_id) === String(variantId)),
        [types, variantId]
    );

    const selectionSummary = useMemo(() => {
        const counts = selectedTokens.reduce((acc, token) => {
            acc[token.product_name] = (acc[token.product_name] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).map(([name, count]) => `${count} x ${name}`).join(", ");
    }, [selectedTokens]);

    const expiryOptions = () => {
        switch (action) {
            case "grant":
                return [
                    { value: "type_rule", label: `Use the token type's rule: ${ruleText(selectedType)}` },
                    { value: "custom", label: "Pick a date" },
                    { value: "none", label: "No expiration" },
                ];
            case "convert":
                return [
                    { value: "keep", label: "Keep each token's current expiration" },
                    { value: "type_rule", label: `Use the new type's rule: ${ruleText(selectedType)}` },
                    { value: "custom", label: "Pick a date" },
                ];
            case "change_expiry":
                return [
                    { value: "custom", label: "Pick a date" },
                    { value: "type_rule", label: "Restart each token's own type rule from today" },
                    { value: "none", label: "No expiration" },
                ];
            default:
                return [];
        }
    };

    const validate = () => {
        if ((action === "grant" || action === "convert") && !variantId) return "Choose a token type.";
        if (action === "grant" && (quantity < 1 || quantity > 50)) return "Grant between 1 and 50 tokens at a time.";
        if (action !== "grant" && !selectedTokens.length) return "Select at least one token.";
        if (expiryMode === "custom" && action !== "void" && !customDate) return "Pick the new expiration date.";
        if (!reason.trim()) return "A reason is required.";
        return null;
    };

    const submit = async () => {
        const problem = validate();
        if (problem) {
            setError(problem);
            return;
        }

        setSubmitting(true);
        setError(null);

        const tokenIds = selectedTokens.map(token => token.id);
        const expDate = expiryMode === "custom" && customDate ? formatISO(endOfDay(customDate)) : null;

        let request;
        let message;
        switch (action) {
            case "grant":
                request = UsersAPI.grantTokens({ user_id: userId, token_variant_id: variantId, quantity, expiry_mode: expiryMode, exp_date: expDate, reason });
                message = `Granted ${quantity} ${selectedType?.product_name} token${quantity > 1 ? "s" : ""}.`;
                break;
            case "convert":
                request = UsersAPI.convertTokens({ user_id: userId, token_ids: tokenIds, token_variant_id: variantId, expiry_mode: expiryMode, exp_date: expDate, reason });
                message = `Converted ${tokenIds.length} token${tokenIds.length > 1 ? "s" : ""} to ${selectedType?.product_name}.`;
                break;
            case "change_expiry":
                request = UsersAPI.changeTokenExpiry({ user_id: userId, token_ids: tokenIds, expiry_mode: expiryMode, exp_date: expDate, reason });
                message = `Changed the expiration of ${tokenIds.length} token${tokenIds.length > 1 ? "s" : ""}.`;
                break;
            case "void":
            default:
                request = UsersAPI.voidTokens({ user_id: userId, token_ids: tokenIds, reason });
                message = `Voided ${tokenIds.length} token${tokenIds.length > 1 ? "s" : ""}.`;
                break;
        }

        try {
            const response = await request;
            if (response?.errors) {
                setError(Array.isArray(response.errors) ? response.errors.join(" ") : String(response.errors));
                setSubmitting(false);
                return;
            }
            onDone(message);
        } catch (ex) {
            console.error(ex);
            setError("Something went wrong saving this change.");
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" data-cy="token-action-modal">
            {/* the theme hides header text, so the title sits in the body like the other profile modals */}
            <Modal.Header closeButton />
            <Modal.Body>
                <h5 className="mb-3" data-cy="token-action-title">{TITLES[action]}</h5>
                {action !== "grant" &&
                    <p data-cy="token-action-selection">
                        <span className="bold">{selectedTokens.length} selected:</span> {selectionSummary}
                    </p>
                }

                {(action === "grant" || action === "convert") &&
                    <Form.Group className="mb-3" controlId="token-action-type">
                        <Form.Label>{action === "convert" ? "Convert to" : "Token type"}</Form.Label>
                        <Form.Control as="select" value={variantId} onChange={(e) => setVariantId(e.target.value)} data-cy="token-action-type">
                            <option value="">Choose a token type</option>
                            {types.map(type => (
                                <option key={type.variant_id} value={type.variant_id}>{typeLabel(type)}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                }

                {action === "grant" &&
                    <Form.Group className="mb-3" controlId="token-action-quantity">
                        <Form.Label>How many</Form.Label>
                        <Form.Control
                            type="number"
                            min={1}
                            max={50}
                            value={quantity}
                            onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 0)}
                            data-cy="token-action-quantity"
                        />
                    </Form.Group>
                }

                {action !== "void" &&
                    <fieldset className="mb-3">
                        <legend className="bold fs-6">Expiration</legend>
                        {expiryOptions().map(option => (
                            <Form.Check
                                key={option.value}
                                type="radio"
                                id={`token-expiry-${option.value}`}
                                name="token-expiry-mode"
                                label={option.label}
                                checked={expiryMode === option.value}
                                onChange={() => setExpiryMode(option.value)}
                            />
                        ))}
                        {expiryMode === "custom" &&
                            <div className="mt-2 ms-4">
                                <DatePicker
                                    popperProps={{positionFixed: true}}
                                    dateFormat="MM/dd/yyyy"
                                    minDate={addDays(new Date(), 1)}
                                    showMonthDropdown
                                    showYearDropdown
                                    selected={customDate}
                                    onChange={setCustomDate}
                                    placeholderText="Select date"
                                />
                            </div>
                        }
                    </fieldset>
                }

                {action === "void" &&
                    <p>Voided tokens expire immediately and move to Expired. Use a refund instead if money should go back to the customer.</p>
                }
                {action === "convert" &&
                    <p className="text-muted small">
                        No money changes hands: the token keeps its original purchase, and renewing subscriptions keep granting their own type.
                    </p>
                }

                <Form.Group controlId="token-action-reason">
                    <Form.Label>Reason (required)</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        maxLength={500}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-cy="token-action-reason"
                    />
                </Form.Group>

                {error && <p className="text-danger mt-3 mb-0" data-cy="token-action-error">{error}</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button
                    variant={action === "void" ? "danger" : "primary"}
                    onClick={submit}
                    disabled={submitting}
                    data-cy="token-action-submit"
                >
                    {submitting ? "Saving..." : TITLES[action]}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default TokenActionModal;
